// src/components/admin/LogsViewer.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  startAfter,
  where,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { logAdminEvent, LOG_LEVELS } from '../../services/loggingService';

const LogsViewer = () => {
  const { currentUser } = useAuth();
  const { error, handleError, clearError } = useErrorHandler();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [users, setUsers] = useState({});
  const [activeUserId, setActiveUserId] = useState(null);
  const [userPopupPosition, setUserPopupPosition] = useState({ top: 0, left: 0 });
  const [activeLogDetails, setActiveLogDetails] = useState(null);
  
  // Refs for closing popups when clicking outside
  const userPopupRef = useRef(null);
  
  // Filtering options
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  
  // Define log levels and categories for filters
  const logLevels = [
    { value: '', label: 'All Levels' },
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error' },
    { value: 'critical', label: 'Critical' }
  ];
  
  const logCategories = [
    { value: '', label: 'All Categories' },
    { value: 'auth', label: 'Authentication' },
    { value: 'search', label: 'Search' },
    { value: 'admin', label: 'Administration' },
    { value: 'security', label: 'Security' },
    { value: 'error', label: 'System Error' }
  ];
  
  // Human-readable action names mapping
  const actionNameMap = {
    'search_initiated': 'Search Started',
    'search_completed': 'Search Completed',
    'view_logs': 'Viewed Activity Logs',
    'view_logs_tab': 'Opened Activity Logs Tab',
    'login': 'User Login',
    'logout': 'User Logout',
    'password_reset': 'Password Reset',
    'user_created': 'User Created',
    'user_updated': 'User Updated',
    'export_results': 'Exported Results',
    'generate_report': 'Generated Report',
  };

  // Human-readable category mapping
  const categoryNameMap = {
    'search': 'Search',
    'admin': 'Administration',
    'auth': 'Authentication',
    'security': 'Security',
    'error': 'System Error'
  };
  
  // Function to get the user-friendly action name
  const getActionName = (action) => {
    if (typeof action !== 'string') {
      // Handle cases where action is not a string (e.g., an object)
      console.warn('Unexpected data type for log action:', action);
      return 'Invalid Action'; // Or try to stringify, but 'Invalid' is safer
    }
    return actionNameMap[action] || action;
  };

  // Function to get the user-friendly category name
  const getCategoryName = (category) => {
    if (typeof category !== 'string') {
      // Handle cases where category is not a string
      console.warn('Unexpected data type for log category:', category);
      return 'Invalid Category';
    }
    return categoryNameMap[category] || category;
  };
  
  // Fetch user details for a specific user ID
  const fetchUserDetails = async (userId) => {
    if (!userId || users[userId]) {
      return;
    }
    
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        setUsers(prevUsers => ({
          ...prevUsers,
          [userId]: {
            name: userSnap.data().displayName || userSnap.data().email?.split('@')[0] || 'Unknown User',
            email: userSnap.data().email || 'No email available',
            ...userSnap.data()
          }
        }));
      } else {
        setUsers(prevUsers => ({
          ...prevUsers,
          [userId]: { name: 'Unknown User', email: 'User not found' }
        }));
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setUsers(prevUsers => ({
        ...prevUsers,
        [userId]: { name: 'Error Loading User', email: 'Error loading user details' }
      }));
    }
  };
  
  // Toggle user popup
  const toggleUserPopup = (event, userId) => {
    event.stopPropagation();
    
    // If clicking on the same user, close the popup
    if (activeUserId === userId) {
      setActiveUserId(null);
      return;
    }
    
    // Set the position for the popup based on the button's position
    const buttonRect = event.currentTarget.getBoundingClientRect();
    setUserPopupPosition({
      top: buttonRect.bottom + window.scrollY,
      left: buttonRect.left + window.scrollX
    });
    
    // Set the active user and fetch details if needed
    setActiveUserId(userId);
    if (userId && !users[userId]) {
      fetchUserDetails(userId);
    }
  };
  
  // Toggle details popup
  const toggleDetailsPopup = (event, log) => {
    event.stopPropagation();
    
    // If clicking on the same log, close the popup
    if (activeLogDetails && activeLogDetails.id === log.id) {
      setActiveLogDetails(null);
      return;
    }
    
    // Set the active log details to show inline
    setActiveLogDetails(log);
  };
  
  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userPopupRef.current && !userPopupRef.current.contains(event.target) &&
          !event.target.classList.contains('user-button')) {
        setActiveUserId(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Fetch logs with the current filter settings
  const fetchLogs = async (loadMore = false) => {
    try {
      clearError();
      setLoading(true);
      
      // Create base query
      const logsRef = collection(db, 'activity-logs');
      let logsQuery = query(logsRef, orderBy('timestamp', 'desc'));
      
      // Apply category filter if selected
      if (selectedCategory) {
        logsQuery = query(logsQuery, where('category', '==', selectedCategory));
      }
      
      // Apply level filter if selected
      if (selectedLevel) {
        logsQuery = query(logsQuery, where('level', '==', selectedLevel));
      }
      
      // Apply date range filters if set
      // Note: This is simplified - implementing date range requires additional work with Firestore
      
      // Apply pagination
      if (loadMore && lastDoc) {
        logsQuery = query(logsQuery, startAfter(lastDoc), limit(25));
      } else {
        logsQuery = query(logsQuery, limit(25));
      }
      
      // Execute query
      const snapshot = await getDocs(logsQuery);
      
      // Process results
      setHasMore(snapshot.size === 25);
      
      if (snapshot.size > 0) {
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        setLastDoc(null);
      }
      
      const fetchedLogs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        formattedTime: doc.data().timestamp ? 
          new Date(doc.data().timestamp.toDate()).toLocaleString() : 'Unknown'
      }));
      
      // Update state
      if (loadMore) {
        setLogs(prevLogs => [...prevLogs, ...fetchedLogs]);
      } else {
        setLogs(fetchedLogs);
      }
      
      // Fetch user details for each unique user ID
      const uniqueUserIds = [...new Set(fetchedLogs.map(log => log.userId).filter(Boolean))];
      uniqueUserIds.forEach(userId => {
        if (!users[userId]) {
          fetchUserDetails(userId);
        }
      });
    } catch (error) {
      handleError(error, { 
        component: 'LogsViewer', 
        action: 'fetchLogs',
        filters: { category: selectedCategory, level: selectedLevel }
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Load logs on mount and when filters change
  useEffect(() => {
    fetchLogs();
    
    // Close popups when filters change
    setActiveUserId(null);
    setActiveLogDetails(null);
  }, [selectedCategory, selectedLevel]); // Intentionally not including dateRange to prevent multiple fetches
  
  // Handle filter changes
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setLastDoc(null); // Reset pagination
  };
  
  const handleLevelChange = (e) => {
    setSelectedLevel(e.target.value);
    setLastDoc(null); // Reset pagination
  };
  
  // Function to get appropriate CSS class for log level
  const getLevelClass = (level) => {
    switch (level) {
      case 'critical':
        return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200';
      case 'error':
        return 'bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
      case 'info':
      default:
        return 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200';
    }
  };
  
  // Get user display name
  const getUserDisplayName = (userId) => {
    if (!userId) return 'Anonymous';
    return users[userId]?.name || 'Loading...';
  };

  // Function to get badge style for log level
  const getLevelBadgeClass = (level) => {
    if (typeof level !== 'string') return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    
    switch (level) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'error':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'info':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  
  // Render logs
  return (
    <div className="space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Activity Logs</h2>
      
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex flex-col">
          <label htmlFor="category-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category:
          </label>
          <select 
            id="category-filter" 
            value={selectedCategory} 
            onChange={handleCategoryChange}
            className="p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {logCategories.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex flex-col">
          <label htmlFor="level-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Level:
          </label>
          <select 
            id="level-filter" 
            value={selectedLevel} 
            onChange={handleLevelChange}
            className="p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {logLevels.map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Date range filters could be added here */}
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-400 p-4 rounded-md">
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Time</th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Level</th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {logs.map((log) => {
                const isActive = activeLogDetails && activeLogDetails.id === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr className={getLevelClass(log.level)}>
                      <td className="p-4 text-sm whitespace-nowrap">{log.formattedTime}</td>
                      <td className="p-4 text-sm whitespace-nowrap">{getCategoryName(log.category)}</td>
                      <td className="p-4 text-sm">{getActionName(log.action)}</td>
                      <td className="p-4 text-sm whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getLevelBadgeClass(log.level)}`}>
                          {typeof log.level === 'string' ? log.level : 'Unknown'}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{getUserDisplayName(log.userId)}</span>
                          <button 
                            className="user-button ml-1 w-5 h-5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded focus:outline-none"
                            onClick={(e) => toggleUserPopup(e, log.userId)}
                            aria-label="View user details"
                          >
                            ▼
                          </button>
                        </div>
                      </td>
                      <td className="p-4 text-sm">
                        <button 
                          className={`details-button px-3 py-1 ${isActive ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'} text-white text-xs rounded transition-colors`}
                          onClick={(e) => toggleDetailsPopup(e, log)}
                          aria-label="View log details"
                          aria-expanded={isActive}
                        >
                          {isActive ? 'Hide Details' : 'View Details'}
                        </button>
                      </td>
                    </tr>
                    {isActive && (
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <td colSpan="6" className="p-0">
                          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                              <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Log Details</h4>
                              <pre className="text-xs font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-2 rounded text-gray-800 dark:text-gray-200 overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-gray-500 dark:text-gray-400">No logs found</td>
                </tr>
              )}
            </tbody>
          </table>
          
          {loading && (
            <div className="p-6 flex justify-center text-gray-500 dark:text-gray-400">
              Loading logs...
            </div>
          )}
          
          {hasMore && !loading && (
            <div className="flex justify-center p-4">
              <button 
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                onClick={() => fetchLogs(true)}
              >
                Load More Logs
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* User details popup */}
      {activeUserId && (
        <div 
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden"
          style={{ top: userPopupPosition.top, left: userPopupPosition.left, width: '280px' }}
          ref={userPopupRef}
        >
          <div className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-800 dark:text-gray-200">User Details</h3>
            <button 
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full focus:outline-none"
              onClick={() => setActiveUserId(null)}
              aria-label="Close user details"
            >
              ×
            </button>
          </div>
          <div className="p-3">
            {users[activeUserId] ? (
              <div className="space-y-2 text-sm">
                <p className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Name:</span> {users[activeUserId].name}</p>
                <p className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Email:</span> {users[activeUserId].email}</p>
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">User ID:</span> 
                  <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 p-1 rounded ml-1">{activeUserId}</span>
                </p>
                {users[activeUserId].role && (
                  <p className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Role:</span> {users[activeUserId].role}</p>
                )}
                {users[activeUserId].isAdmin !== undefined && (
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="font-semibold">Admin:</span> {users[activeUserId].isAdmin ? 'Yes' : 'No'}
                  </p>
                )}
              </div>
            ) : (
              <div className="py-4 text-center text-gray-500 dark:text-gray-400">Loading user details...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsViewer;
